// @flow
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ComponentType } from 'react';

type Id = string | number;

export type DragObject = {
  id: Id,
  position?: number,
  component: Object // component to move, copy, create
}

type Props = {};

type State = {
  dragObject?: DragObject,
  callback?: (DragObject, DragObject) => void
}

type Context = {
  setDropZone: Function,
  onDragStartHandler: Function,
  onDropHandler: Function,
  onDragEndHandler: Function,
  registerOnDropCallback: Function
}

// Generate unique window ID on page load
// Used for cross-window drag/drop
const windowId = uuidv4();

export const DragAndDropContext = React.createContext<Context>({
  setDropZone: () => {},
  onDragStartHandler: () => {},
  onDropHandler: () => {},
  onDragEndHandler: () => {},
  registerOnDropCallback: () => {}
});

// https://material.io/resources/icons/?icon=file_copy&style=baseline
const copySVG = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDB6Ii8+PHBhdGggZD0iTTE2IDFINGMtMS4xIDAtMiAuOS0yIDJ2MTRoMlYzaDEyVjF6bS0xIDRsNiA2djEwYzAgMS4xLS45IDItMiAySDcuOTlDNi44OSAyMyA2IDIyLjEgNiAyMWwuMDEtMTRjMC0xLjEuODktMiAxLjk5LTJoN3ptLTEgN2g1LjVMMTQgNi41VjEyeiIvPjwvc3ZnPg==';

/**

Usage:
- Wrap the root component with DragAndDrop HOC to maintain a shared state
- Register a callback in the root component
- Use DragAndDrop.Consumer/useContext(DragAndDropContext) to pass event handlers to child components

*/
function withDragAndDrop(WrappedComponent: ComponentType<*>) {
  class DragAndDrop extends React.Component<Props, State> {
    state = {
      callback: undefined
    };

    image: HTMLImageElement

    componentDidMount() {
      this.image = document.createElement('img');

      // Loading copy image as SVG
      this.image.src = copySVG;
    }

    setDropZone = (e: DragEvent) => e.preventDefault();

    onDragStartHandler = (e: DragEvent, dragObject: DragObject) => {
      e.stopPropagation();

      e.dataTransfer.setDragImage(this.image, 10, 10);

      // Store serialized component in DataTransfer to move between windows
      e.dataTransfer.setData('text/plain', JSON.stringify({ windowId, ...dragObject, }));
    };

    onDropHandler = (e: DragEvent, dropObject: DragObject) => {
      e.stopPropagation();

      const { callback } = this.state;

      // Retrieve serialized component from DataTransfer
      const { windowId: dragWindowId, ...dragObject } = JSON.parse(e.dataTransfer.getData('text/plain'));

      // Drag/drop same window if drag + drop object are defined and windowId matches.
      // Or create new component from data, if data is present
      if (dragWindowId === windowId &&
          dragObject && typeof dragObject.id !== 'undefined' &&
          dropObject && typeof dropObject.id !== 'undefined' &&
          dragObject.id !== dropObject.id) {
        if (callback) {
          callback(dragObject, dropObject);
        }
      } else if (dragObject && dragObject.component &&
        dropObject && typeof dropObject.id !== 'undefined') {
        if (callback) {
          callback({ id: 'NEW', component: dragObject.component }, dropObject);
        }
      }
    };

    onDragEndHandler = () => this.setState({ dragObject: undefined });

    registerOnDropCallback = (callback: (DragObject, DragObject) => void) => this.setState({ callback });

    render() {
      const {
        setDropZone,
        onDragStartHandler,
        onDropHandler,
        onDragEndHandler,
        registerOnDropCallback
      } = this;

      return (
        <DragAndDropContext.Provider
          value={{
            setDropZone,
            onDragStartHandler,
            onDropHandler,
            onDragEndHandler,
            registerOnDropCallback
          }}
        >
          <WrappedComponent
            {...this.props}
            registerOnDropCallback={registerOnDropCallback}
          />
        </DragAndDropContext.Provider>
      );
    }
  }

  return DragAndDrop;
}

export default withDragAndDrop;
